// src/pages/lab/processing/processingtabs/components/ProtocolInstanceModal.tsx
//
// Group processing drawer — three columns, matching the treatment plan drawer:
//   1. Group + protocol context (210px)
//   2. Step rail in flow order, branches indented (200px)
//   3. The selected step's execution surface, or the group roster
//
// Step execution renders inline in column 3 rather than opening a second
// stacked drawer, so context stays on screen while a technician works.

import React, { useEffect, useMemo, useState } from "react";
import { getSessionUser } from "../../../../../utils/auth/sessionUser";
import { Button, Checkbox, Input, Modal, Radio, Space, Typography } from "antd";
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	PauseCircleOutlined,
	PlayCircleOutlined,
} from "@ant-design/icons";

// API
import {
	completeGroupProcessing,
	failGroupInstance,
	markGroupAsFailed,
	putGroupOnHold,
	removeItemFromGroup,
	resumeGroupFromHold,
	startGroupProcessing,
} from "../../../../../api/labiq/GroupProcessingApi";
import { fetchProtocol } from "../../../../../api/labiq/ProtocalApi";
import { fetchAllTeamMembers } from "../../../../../api/admin/teamApi";

// Types
import type {
	AnalysisProtocolInstance,
	GroupStepInstance,
	ProcessingGroup,
	Protocol,
	ProtocolStep,
	SampleProtocolInstance,
} from "../../../../../types/lab/ProtocolTypes";
import type { TeamMember } from "../../../../../types/admin/TeamTypes";

// Shared components
import { DetailDrawer, EmptyState } from "../../../../../components/shared";

// Drawer columns
import GroupMetaPanel from "./instanceDrawer/GroupMetaPanel";
import GroupInstancesPanel from "./instanceDrawer/GroupInstancesPanel";
import StepExecutionPanel from "./instanceDrawer/StepExecutionPanel";
import StepRail, { RailSelection } from "./instanceDrawer/StepRail";
import { computeUntakenStepIds } from "./instanceDrawer/flowTree";
import { AUTO_STEP_TYPES } from "./instanceDrawer/stepMeta";
import { canOpenStep as isStepOpenable } from "./instanceDrawer/stepAccess";

// Hooks
import { useNotifications } from "../../../../../context/NotificationContext";
import { requireLabiqUserId } from "../../../../../utils/auth/labiqUserId";

const { Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

type GroupInstance = SampleProtocolInstance | AnalysisProtocolInstance;

interface ProtocolInstanceModalProps {
	group: ProcessingGroup;
	groupType: "sample" | "analysis";
	onClose: () => void;
	onRefresh: () => void;
	isOpenModal: boolean;
}

const ProtocolInstanceModal: React.FC<ProtocolInstanceModalProps> = ({
	group,
	groupType,
	onClose,
	onRefresh,
	isOpenModal,
}) => {
	const [processingData, setProcessingData] = useState<ProcessingGroup>(group);
	const [stepData, setStepData] = useState<GroupStepInstance[]>(group!.group_step_instances || []);
	const [activeStep, setActiveStep] = useState(processingData.current_step || 0);
	const [actionModalOpen, setActionModalOpen] = useState(false);
	const [actionType, setActionType] = useState<"hold" | "fail" | "complete" | null>(null);
	const [actionNotes, setActionNotes] = useState("");
	const [finishSamples, setFinishSamples] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [assignedTech, setAssignedTech] = useState<TeamMember>();

	/** Column 3 target. Null falls back to the step the group is sitting on. */
	const [selection, setSelection] = useState<RailSelection | null>(null);
	const [exportModalOpen, setExportModalOpen] = useState(false);
	const [exportType, setExportType] = useState<"sample_ids" | "analysis_ids">("sample_ids");
	/** Tracks which instance ID is currently having the "Fail" modal open. */
	const [failInstanceId, setFailInstanceId] = useState<number | null>(null);
	const [failReason, setFailReason] = useState("");
	const [failInstanceType, setFailInstanceType] = useState<"sample" | "analysis">("sample");

	const { showSuccess, showError } = useNotifications();
	const user = getSessionUser();

	// Fetch the full protocol (including steps) when the group's embedded protocol
	// doesn't carry the steps array — the list endpoint only returns protocol metadata.
	useEffect(() => {
		if (!processingData.protocol?.steps?.length && group.protocol_id) {
			fetchProtocol(group.protocol_id)
				.then((protocol: Protocol) => {
					setProcessingData((prev) => ({
						...prev,
						protocol: { ...prev.protocol!, steps: protocol.steps },
					}));
				})
				.catch((err) => console.error("Failed to load protocol steps:", err));
		}
	}, [group.id]);

	useEffect(() => {
		if (!stepData.length && processingData.protocol?.steps) {
			const initialStepData: GroupStepInstance[] = processingData.protocol.steps.map((step) => {
				const existingStepData = group!.group_step_instances?.find((sd) => sd.step_id === step.id);

				let status;

				if (existingStepData) {
					status = existingStepData.status;
				} else {
					const stepNumber = step.step_number;
					if (stepNumber < activeStep) {
						status = "completed";
					} else if (stepNumber === activeStep && processingData.status === "active") {
						const hasInProgressStep = group!.group_step_instances?.some(
							(sd) => sd.status === "in_progress",
						);
						status = hasInProgressStep ? "in_progress" : "pending";
					} else {
						status = "pending";
					}
				}

				return {
					id: existingStepData?.id || 0,
					group_id: group!.id,
					step_id: step.id,
					status,
					field_values: existingStepData?.field_values || {},
					created_at: existingStepData?.created_at || new Date().toISOString(),
					updated_at: existingStepData?.updated_at || new Date().toISOString(),
					step: step,
					started_at: existingStepData?.started_at,
					completed_at: existingStepData?.completed_at,
					notes: existingStepData?.notes,
				} as GroupStepInstance;
			});
			setStepData(initialStepData);
		}

		loadTech();
		// processingData.protocol?.steps?.length is intentionally included so this
		// effect re-runs after the fetch-protocol effect populates the steps array.
	}, [activeStep, stepData.length, processingData.protocol?.steps?.length]);

	const loadTech = async () => {
		try {
			// labiq's assigned_to is the legacy integer id; the staff directory carries it beside the uuid.
			const assignedTo = processingData.assigned_to;
			if (assignedTo) {
				const { data } = await fetchAllTeamMembers();
				setAssignedTech(data.find((member) => member.legacy_user_id === assignedTo));
			}
		} catch (error) {
			console.warn("Could not load tech data:", error);
		}
	};

	const steps = processingData.protocol?.steps ?? [];

	const activeStepNumbers: number[] = useMemo(
		() =>
			processingData.active_step_numbers ??
			(processingData.current_step ? [processingData.current_step] : []),
		[processingData.active_step_numbers, processingData.current_step],
	);

	const canOpenStep = (step: ProtocolStep): boolean =>
		isStepOpenable(
			step,
			stepData.find((s) => s.step_id === step.id)?.status,
			processingData.status,
			activeStepNumbers,
		);

	/**
	 * The step column 3 shows when the technician hasn't picked one: the first
	 * open step on any branch, else the last one they completed.
	 */
	const derivedStep: ProtocolStep | undefined = useMemo(() => {
		// A completed auto node is openable but never the default, or a finished group lands on its End node.
		const openable = steps.filter((s) => !AUTO_STEP_TYPES.has(s.step_type) && canOpenStep(s));
		if (openable.length === 0) return undefined;

		const live = openable.find((s) => {
			const gsi = stepData.find((sd) => sd.step_id === s.id);
			return gsi?.status === "in_progress" || activeStepNumbers.includes(s.step_number);
		});
		return live ?? openable[openable.length - 1];
	}, [steps, stepData, activeStepNumbers, processingData.status]);

	const selectedStep: ProtocolStep | undefined =
		selection?.kind === "step" ? steps.find((s) => s.id === selection.stepId) : derivedStep;

	const effectiveSelection: RailSelection | null =
		selection?.kind === "instances"
			? selection
			: selectedStep
			? { kind: "step", stepId: selectedStep.id }
			: null;

	const selectedStepData = selectedStep
		? stepData.find((s) => s.step_id === selectedStep.id)
		: undefined;

	/**
	 * Steps this group will never reach because the conditional above them sent
	 * every instance down the other branch. They are dropped from the rail and
	 * must not gate completion — an untaken branch stays `pending` forever, which
	 * previously left a finished group with no way to be completed.
	 */
	const untakenStepIds = useMemo(
		() => computeUntakenStepIds(steps, stepData),
		[steps, stepData],
	);

	/**
	 * The steps this run actually has to do: start / end markers aren't work, and
	 * an untaken branch isn't this group's path. Counting anything else makes the
	 * header disagree with the rail.
	 */
	const liveSteps = useMemo(
		() =>
			steps.filter(
				(s) => s.step_type !== "start" && s.step_type !== "end" && !untakenStepIds.has(s.id),
			),
		[steps, untakenStepIds],
	);
	const liveStepIds = useMemo(() => new Set(liveSteps.map((s) => s.id)), [liveSteps]);
	const completedSteps = stepData.filter(
		(s) => s.status === "completed" && liveStepIds.has(s.step_id),
	).length;

	const instances: GroupInstance[] =
		groupType === "sample"
			? processingData.sample_instances ?? []
			: processingData.analysis_instances ?? [];

	const areAllStepsCompleted = (): boolean => {
		if (stepData.length === 0 || liveSteps.length === 0) return false;

		return liveSteps.every((s) => {
			const status = stepData.find((sd) => sd.step_id === s.id)?.status;
			// `skipped` is terminal too — it will never become `completed`.
			return status === "completed" || status === "skipped";
		});
	};

	const hasAliquotStep = (): boolean =>
		processingData.protocol?.steps?.some((step) => step.step_type === "aliquot") || false;

	const generateAnalysisId = (sampleId: string, index: number = 0, analysisType: string): string => {
		// Find the aliquot step in the protocol
		const aliquotStep = processingData.protocol?.steps?.find((step) => step.step_type === "aliquot");

		if (!aliquotStep || !aliquotStep.configuration) {
			return sampleId; // Fallback to sample ID if no aliquot config found
		}

		// Get aliquot configuration from step configuration
		const aliquotConfig = (aliquotStep.configuration as any)?.aliquot_configuration;

		if (!aliquotConfig) {
			return sampleId; // Fallback if no aliquot_configuration found
		}

		const prefix = aliquotConfig.label_prefix || "";
		const suffix = aliquotConfig.label_suffix || "";

		// Process template variables in both prefix and suffix
		const processTemplate = (template: string, currentIndex: number): string => {
			const now = new Date();

			return (
				template
					// {{incremental}} → 001, 002, 003...
					.replace(/\{\{incremental\}\}/g, (currentIndex + 1).toString().padStart(3, "0"))
					// {{analysis}} → PCR, ELISA, WesternBlot
					.replace(/\{\{analysis\}\}/g, analysisType)
					// {{analysis_F}} → P, E, W (first letter)
					.replace(/\{\{analysis_F\}\}/g, analysisType.charAt(0).toUpperCase())
					// {{date}} → 2026-01-13
					.replace(/\{\{date\}\}/g, now.toISOString().split("T")[0])
					// {{time}} → 14:30:45
					.replace(/\{\{time\}\}/g, now.toTimeString().split(" ")[0])
					// {{sample_id}} → Original sample ID
					.replace(/\{\{sample_id\}\}/g, sampleId)
			);
		};

		return `${processTemplate(prefix, index)}${sampleId}${processTemplate(suffix, index)}`;
	};

	const handleInstanceAction = (type: "hold" | "fail" | "complete") => {
		setActionType(type);
		setActionNotes("");
		setFinishSamples(false);
		setActionModalOpen(true);
	};

	const executeInstanceAction = async () => {
		if (!actionType) return;

		setProcessing(true);
		try {
			switch (actionType) {
				case "hold":
					await putGroupOnHold(group!.id, { reason: actionNotes });
					showSuccess("Group put on hold");
					break;

				case "fail":
					await markGroupAsFailed(group!.id, { reason: actionNotes });
					showSuccess("Group marked as failed");
					break;

				case "complete": {
					const performedBy = processingData.assigned_to || 1;
					await completeGroupProcessing(group!.id, {
						performed_by: performedBy,
						notes: actionNotes,
						finish_samples: finishSamples,
					});
					showSuccess("Group processing completed");
					break;
				}
			}

			setActionModalOpen(false);
			onRefresh();
		} catch (error) {
			showError(`Failed to ${actionType} group`);
			console.error(`Group ${actionType} error:`, error);
		} finally {
			setProcessing(false);
		}
	};

	const handleResumeFromHold = async () => {
		try {
			await resumeGroupFromHold(group!.id);
			showSuccess("Group resumed");
			onRefresh();
		} catch (error) {
			showError("Failed to resume group");
		}
	};

	const handleStartGroupProcessing = async () => {
		confirm({
			title: "Start processing",
			content: `This activates "${group!.group_name}" and makes its steps available for execution.`,
			okText: "Start processing",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					setProcessing(true);
					const updatedGroup = await startGroupProcessing(group!.id, {
						performed_by: requireLabiqUserId(user),
					});
					setProcessingData(updatedGroup);
					setActiveStep(updatedGroup.current_step || 0);
					showSuccess("Group processing started");
				} catch (error) {
					showError("Failed to start group processing");
				} finally {
					setProcessing(false);
				}
			},
		});
	};

	const handleFailInstance = async () => {
		if (!failInstanceId || !failReason.trim()) {
			showError("Enter a reason before failing this sample.");
			return;
		}

		try {
			setProcessing(true);
			await failGroupInstance(group.id, failInstanceId, {
				reason: failReason,
				performed_by: requireLabiqUserId(user),
				instance_type: failInstanceType,
			});
			showSuccess(failInstanceType === "analysis" ? "Analysis failed" : "Sample failed");
			setFailInstanceId(null);
			setFailReason("");
			onRefresh();
		} catch (error) {
			showError(
				failInstanceType === "analysis"
					? "Failed to mark analysis as failed"
					: "Failed to mark sample as failed",
			);
		} finally {
			setProcessing(false);
		}
	};

	const handleRemoveItem = async (item: GroupInstance) => {
		try {
			let updatedGroup;
			if ("sample_id" in item) {
				updatedGroup = await removeItemFromGroup(group.id, item.sample_id, "sample");
			} else if ("analysis_id" in item) {
				updatedGroup = await removeItemFromGroup(group.id, item.analysis_id as any, "analysis");
			}

			showSuccess(groupType === "analysis" ? "Analysis removed from group" : "Sample removed from group");
			reloadModalData(updatedGroup, false);
		} catch (error) {
			showError(
				groupType === "analysis"
					? "Failed to remove analysis from group"
					: "Failed to remove sample from group",
			);
		}
	};

	const handleExportSamples = () => {
		if (!group!.sample_instances || group!.sample_instances.length === 0) {
			showError("No samples available to export");
			return;
		}

		// Check if protocol has aliquot steps
		if (hasAliquotStep()) {
			// Show modal to let user choose export type
			setExportModalOpen(true);
		} else {
			// Direct export with sample IDs
			performCsvExport("sample_ids");
		}
	};

	const performCsvExport = (type: "sample_ids" | "analysis_ids") => {
		if (!group!.sample_instances || group!.sample_instances.length === 0) {
			return;
		}

		const headers = [type === "sample_ids" ? "Sample_ID" : "Analysis_ID"];
		const csvRows = [headers.join(",")];

		group!.sample_instances.forEach((instance) => {
			instance.sample.sampleOA.analysis_requested.forEach((analysis, index) => {
				const id =
					type === "sample_ids"
						? instance.sample_id
						: generateAnalysisId(instance.sample_id, index, analysis);
				csvRows.push([id].join(","));
			});
		});

		// Use CRLF line endings for Windows compatibility
		const csvContent = csvRows.join("\r\n");

		// Add BOM for proper UTF-8 encoding across all systems
		const BOM = "\uFEFF";
		const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });

		// Generate cross-platform safe filename
		const timestamp = new Date().toISOString().split("T")[0];
		const idType = type === "sample_ids" ? "samples" : "analysis";
		const filename = `group_${idType}_${group!.group_name.replace(/\s+/g, "_")}_${timestamp}.csv`;

		// Use modern download approach with fallback
		if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
			// IE/Edge fallback
			(window.navigator as any).msSaveOrOpenBlob(blob, filename);
		} else {
			const link = document.createElement("a");
			const url = URL.createObjectURL(blob);

			link.setAttribute("href", url);
			link.setAttribute("download", filename);
			link.style.visibility = "hidden";

			document.body.appendChild(link);
			link.click();

			// Clean up
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		}

		showSuccess(
			`Exported ${group!.sample_instances.length} ${
				type === "sample_ids" ? "sample IDs" : "analysis IDs"
			} to CSV`,
		);
		setExportModalOpen(false);
	};

	const reloadModalData = (groupData?: ProcessingGroup, skipParentRefresh?: boolean) => {
		setStepData(groupData!.group_step_instances || []);
		setProcessingData((prevData) => ({
			...groupData!,
			protocol: groupData!.protocol || prevData.protocol,
		}));
		setActiveStep(groupData!.current_step || 0);

		if (!skipParentRefresh) {
			onRefresh();
		}
	};

	/* ─── Header ─────────────────────────────────────────────────────────── */

	const headerActions = (
		<Space size={8}>
			{processingData.status === "pending" && (
				<Button
					type="primary"
					size="small"
					icon={<PlayCircleOutlined />}
					onClick={handleStartGroupProcessing}
					loading={processing}
				>
					Start processing
				</Button>
			)}
			{processingData.status === "active" && (
				<>
					{areAllStepsCompleted() && (
						<Button
							type="primary"
							size="small"
							icon={<CheckCircleOutlined />}
							onClick={() => handleInstanceAction("complete")}
						>
							Complete
						</Button>
					)}
					<Button size="small" icon={<PauseCircleOutlined />} onClick={() => handleInstanceAction("hold")}>
						Hold
					</Button>
					<Button
						size="small"
						danger
						icon={<CloseCircleOutlined />}
						onClick={() => handleInstanceAction("fail")}
					>
						Fail
					</Button>
				</>
			)}
			{processingData.status === "on_hold" && (
				<Button
					type="primary"
					size="small"
					icon={<PlayCircleOutlined />}
					onClick={handleResumeFromHold}
				>
					Resume
				</Button>
			)}
		</Space>
	);

	const subtitle = [
		processingData.protocol?.name,
		processingData.protocol?.version ? `v${processingData.protocol.version}` : null,
		`${instances.length} ${groupType === "analysis" ? "analyses" : "samples"}`,
		liveSteps.length > 0 ? `${completedSteps} of ${liveSteps.length} steps` : null,
	]
		.filter(Boolean)
		.join(" · ");

	/* ─── Column 3 ───────────────────────────────────────────────────────── */

	const detailContent = (() => {
		if (effectiveSelection?.kind === "instances") {
			return (
				<GroupInstancesPanel
					group={processingData}
					groupType={groupType}
					groupIsActive={processingData.status === "active"}
					onExport={handleExportSamples}
					onFail={(instance) => {
						setFailInstanceId(instance.id);
						setFailReason("");
						setFailInstanceType(groupType);
					}}
					onRemove={handleRemoveItem}
				/>
			);
		}

		if (selectedStep) {
			return (
				<StepExecutionPanel
					key={selectedStep.id}
					group={processingData}
					step={selectedStep}
					stepData={selectedStepData}
					onRefresh={reloadModalData}
					onStepCompleted={() => setSelection(null)}
				/>
			);
		}

		return (
			<div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
				<EmptyState
					title={
						processingData.status === "pending"
							? "Processing not started"
							: processingData.status === "on_hold"
							? "Group on hold"
							: processingData.status === "completed"
							? "All steps complete"
							: "No step available"
					}
					description={
						processingData.status === "pending"
							? "Start processing to open the protocol's steps."
							: processingData.status === "on_hold"
							? "Resume the group to continue working its steps."
							: processingData.status === "completed"
							? "Every step in this protocol has been completed for the group."
							: "Steps open as the group advances through the protocol."
					}
				/>
			</div>
		);
	})();

	/* ─── Render ─────────────────────────────────────────────────────────── */

	return (
		<>
			<DetailDrawer
				title={group!.group_name}
				subtitle={subtitle}
				open={isOpenModal}
				onClose={onClose}
				width="85vw"
				noPadding
				headerActions={headerActions}
			>
				<div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
					{/* Col 1 — group + protocol context */}
					<GroupMetaPanel
						group={processingData}
						technician={assignedTech}
						instanceCount={instances.length}
						instanceLabel={groupType === "analysis" ? "analyses" : "samples"}
						completedSteps={completedSteps}
						totalSteps={liveSteps.length}
					/>

					{/* Col 2 — step rail */}
					<StepRail
						steps={steps}
						stepInstances={stepData}
						activeStepNumbers={activeStepNumbers}
						instanceCount={instances.length}
						instanceLabel={groupType === "analysis" ? "Analyses" : "Samples"}
						selection={effectiveSelection}
						onSelect={setSelection}
						canOpenStep={canOpenStep}
						hiddenStepIds={untakenStepIds}
					/>

					{/* Col 3 — selected step or group roster */}
					<div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
						{detailContent}
					</div>
				</div>
			</DetailDrawer>

			{/* Export type selection */}
			<Modal
				title="Export sample data"
				open={exportModalOpen}
				onCancel={() => setExportModalOpen(false)}
				onOk={() => performCsvExport(exportType)}
				okText="Export CSV"
				cancelText="Cancel"
				width={480}
				destroyOnClose
			>
				<Space direction="vertical" size="middle" style={{ width: "100%" }}>
					<Text style={{ fontSize: 12 }}>
						This protocol aliquots samples. Choose which identifiers to export.
					</Text>

					<Radio.Group
						value={exportType}
						onChange={(e) => setExportType(e.target.value)}
						style={{ width: "100%" }}
					>
						<Space direction="vertical" size="small">
							<Radio value="sample_ids">
								<Text style={{ fontSize: 12, fontWeight: 500 }}>Sample IDs</Text>
								<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
									The original sample identifiers
								</Text>
							</Radio>
							<Radio value="analysis_ids">
								<Text style={{ fontSize: 12, fontWeight: 500 }}>Analysis IDs for aliquoting</Text>
								<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
									Sample IDs with the aliquot prefixes and suffixes applied
								</Text>
							</Radio>
						</Space>
					</Radio.Group>

					<div
						style={{
							padding: "8px 10px",
							borderRadius: "var(--mg-radius-sm)",
							border: "1px solid var(--mg-border)",
							background: "var(--mg-bg-subtle)",
						}}
					>
						<Text type="secondary" style={{ fontSize: 11 }}>
							Preview ·{" "}
							{exportType === "sample_ids"
								? group?.sample_instances?.[0]?.sample_id ?? "Sample_ID"
								: hasAliquotStep() && group?.sample_instances?.length
								? generateAnalysisId(
										group.sample_instances[0].sample_id,
										0,
										group.sample_instances[0].sample?.sampleOA?.analysis_requested[0],
								  )
								: "Analysis_ID with aliquot configuration applied"}
						</Text>
					</div>
				</Space>
			</Modal>

			{/* Fail an individual sample / analysis */}
			<Modal
				title={failInstanceType === "analysis" ? "Fail analysis" : "Fail sample"}
				open={failInstanceId !== null}
				onCancel={() => {
					setFailInstanceId(null);
					setFailReason("");
				}}
				onOk={handleFailInstance}
				confirmLoading={processing}
				okText={failInstanceType === "analysis" ? "Fail analysis" : "Fail sample"}
				okButtonProps={{ danger: true, disabled: !failReason.trim() }}
				width={480}
				destroyOnClose
			>
				<Space direction="vertical" size="middle" style={{ width: "100%" }}>
					<div>
						<div style={{ fontSize: 10, color: "var(--mg-text-secondary)", marginBottom: 4 }}>
							Reason (required)
						</div>
						<TextArea
							value={failReason}
							onChange={(e) => setFailReason(e.target.value)}
							placeholder="Insufficient volume, contamination, equipment failure…"
							rows={3}
							autoFocus
						/>
					</div>
					<Text type="secondary" style={{ fontSize: 11 }}>
						This {failInstanceType === "analysis" ? "analysis" : "sample"} leaves active processing.
						The rest of the group continues unaffected.
					</Text>
				</Space>
			</Modal>

			{/* Hold / fail / complete the whole group */}
			<Modal
				title={
					actionType === "hold"
						? "Put group on hold"
						: actionType === "fail"
						? "Mark group as failed"
						: "Complete group processing"
				}
				open={actionModalOpen}
				onCancel={() => setActionModalOpen(false)}
				onOk={executeInstanceAction}
				confirmLoading={processing}
				okText={
					actionType === "hold"
						? "Put on hold"
						: actionType === "fail"
						? "Mark as failed"
						: "Complete processing"
				}
				okButtonProps={{
					danger: actionType === "fail",
					disabled: actionType !== "complete" && !actionNotes.trim(),
				}}
				width={480}
				destroyOnClose
			>
				<Space direction="vertical" size="middle" style={{ width: "100%" }}>
					<div>
						<div style={{ fontSize: 10, color: "var(--mg-text-secondary)", marginBottom: 4 }}>
							{actionType === "hold"
								? "Reason for hold (required)"
								: actionType === "fail"
								? "Failure reason (required)"
								: "Completion notes (optional)"}
						</div>
						<TextArea
							value={actionNotes}
							onChange={(e) => setActionNotes(e.target.value)}
							placeholder={
								actionType === "hold"
									? `Why "${group.group_name}" is being held`
									: actionType === "fail"
									? `Why "${group.group_name}" failed`
									: "Anything worth recording about this run"
							}
							rows={3}
							autoFocus
						/>
					</div>

					{actionType === "complete" && groupType === "analysis" && (
						<Checkbox checked={finishSamples} onChange={(e) => setFinishSamples(e.target.checked)}>
							<Text style={{ fontSize: 12 }}>Mark all samples in the group as finished</Text>
						</Checkbox>
					)}
				</Space>
			</Modal>
		</>
	);
};

export default ProtocolInstanceModal;
